import React, { useState, useEffect, useRef } from "react";
import { RelativeDate } from "components/canvas/detailsSide/utils/RelativeDte";
import {
  Notification,
  NotificationTypeJS,
  NotificationStatus,
} from "target_wasm";
import DoneIcon from "@mui/icons-material/Done";
import {
  acceptTableRequest,
  declineTableRequest,
  respondToTransferOffer,
  markNotificationAsSeen,
} from "./NotificationsApi";
import { respondToOrgInvitation } from "@/lib/api/organizations";
import { useOrgContextStore } from "components/main/stores/orgContextStore";
import { createLogger } from "debug/DebugingLogger";
import { useNavigate } from "react-router";
import { NotificationMessage } from "./NotificationMessage";
import { NotificationIcon } from "./NotificationIcon";
import { RequestActions } from "./RequestActions";
import { useNavigation } from "contexts/NavigationProvider";
import { useTeamsStore } from "components/main/stores/teamsStore";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { useCopyTable } from "components/canvas/detailsSide/tableDetails/TableServices";
import { CopyActionButton } from "components/common/CopyActionButton";
import { ModelPicker } from "components/common/ModelPicker";

const log = createLogger("NotificationItem");

interface NotificationItemProps {
  notification: Notification;
  onClick: () => void;
  onNotificationUpdate?: () => void;
  markedAsSeen?: boolean;
  onNavigateToTeam?: () => void;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClick,
  onNotificationUpdate,
  markedAsSeen,
  onNavigateToTeam,
}) => {
  const isUnread = notification.status_js() === NotificationStatus.Pending;
  const notificationType = notification.notification_type_js();
  const notificationData = notification.get_data_js();
  const emailDetails = notificationData.in_email_details;
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDoneIcon, setShowDoneIcon] = useState(false);
  const [localRequestStatus, setLocalRequestStatus] = useState<string | null>(
    null,
  );

  const deletionStatus = notificationData.status as string | undefined;
  const isTableDeleted = deletionStatus?.toLowerCase() === "deleted";
  const isCopyableNotification =
    notificationType === NotificationTypeJS.TableScheduledForDeletion &&
    deletionStatus?.toLowerCase() !== "cancelled" &&
    !isTableDeleted;

  const tableId = notificationData.table_details?.table_id;

  const { status: copyStatus, copy, canCopy } = useCopyTable(tableId);

  const [showModelPicker, setShowModelPicker] = useState(false);
  const [pickerMode, setPickerMode] = useState<"transfer" | "copy">("transfer");
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (markedAsSeen) {
      setShowDoneIcon(true);
      const timer = setTimeout(() => {
        setShowDoneIcon(false);
      }, 4000);

      return () => clearTimeout(timer);
    } else {
      setShowDoneIcon(false);
    }
  }, [markedAsSeen]);

  const navigate = useNavigate();
  const { refreshTeams } = useNavigation();
  const fetchTeamsList = useTeamsStore((state) => state.fetchTeamsList);

  const markAsSeen = async () => {
    if (notification.status_js() === NotificationStatus.Pending) {
      await markNotificationAsSeen(notification.notification_id_js());
    }
  };

  const importingModelIds: string[] =
    notificationData.importing_model_ids ?? [];

  const handleOpenCopyPicker = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (showModelPicker) {
      setShowModelPicker(false);
      return;
    }
    setPickerMode("copy");
    setShowModelPicker(true);
  };

  const handleSelectModelForCopy = async (modelId: string) => {
    setShowModelPicker(false);
    if (!tableId) return;
    try {
      const importedVersion = notificationData.imported_version as
        number | undefined;
      await copy(modelId, importedVersion);
      await markAsSeen();
      onNotificationUpdate?.();
    } catch (error) {
      log.error("Failed to copy table:", error);
    }
  };

  const processRequest = async (
    action: () => Promise<unknown>,
    statusLabel: string,
    errorMsg: string,
  ) => {
    setIsProcessing(true);
    try {
      await action();
      setLocalRequestStatus(statusLabel);
      await markAsSeen();
      onNotificationUpdate?.();
    } catch (error) {
      log.error(errorMsg, error);
    } finally {
      setIsProcessing(false);
    }
  };

  const respondToOrgInvite = async (accept: boolean) => {
    const orgId = notificationData.org_id as string | undefined;
    if (!orgId) return;
    setIsProcessing(true);
    try {
      await respondToOrgInvitation(orgId, accept);
      const store = useOrgContextStore.getState();
      await Promise.all([store.refreshInvites(), store.refreshOrgs()]);
      setLocalRequestStatus(accept ? "joined" : "declined");
      await markAsSeen();
      onNotificationUpdate?.();
    } catch (error) {
      // Usually the invite was answered elsewhere — say so and re-sync, or
      // the card sits there looking like the click did nothing.
      await useOrgContextStore.getState().refreshInvites();
      showNotificationGlobal(
        error instanceof Error ? error.message : "Could not respond",
        "error",
      );
      log.error("Failed to respond to org invitation:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAcceptRequest = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (notificationType === NotificationTypeJS.OrgInvite) {
      await respondToOrgInvite(true);
      return;
    }
    if (notificationType === NotificationTypeJS.TableRequest) {
      const tableId = notificationData.table_details?.table_id;
      const tableName = notificationData.table_details?.table_name;
      const targetModelId = notificationData.target_model;
      const tableVersion = notificationData.table_version?.version || 1;
      const requesterId = emailDetails?.from_client_id;

      if (tableId && targetModelId && requesterId && tableName) {
        await processRequest(
          () =>
            acceptTableRequest(
              tableId,
              tableName,
              targetModelId,
              tableVersion,
              emailDetails.from_email,
            ),
          "accepted",
          "Failed to accept table request:",
        );
      }
    } else if (
      notificationType === NotificationTypeJS.TableOwnershipTransferRequest
    ) {
      e.stopPropagation();
      if (showModelPicker) {
        setShowModelPicker(false);
        return;
      }
      setPickerMode("transfer");
      setShowModelPicker(true);
    }
  };

  const handleSelectModelForTransfer = async (modelId: string) => {
    setShowModelPicker(false);
    const notificationId = notification.notification_id_js();
    await processRequest(
      () =>
        respondToTransferOffer(notificationId, {
          type: "Accept",
          to_model: modelId,
        }),
      "accepted",
      "Failed to accept transfer offer:",
    );
  };

  const handleDeclineRequest = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (notificationType === NotificationTypeJS.OrgInvite) {
      await respondToOrgInvite(false);
      return;
    }
    if (notificationType === NotificationTypeJS.TableRequest) {
      const tableId = notificationData.table_details?.table_id;
      const tableName = notificationData.table_details?.table_name;
      const targetModelId = notificationData.target_model;
      const requesterId = emailDetails?.from_client_id;

      if (tableId && targetModelId && requesterId && tableName) {
        await processRequest(
          () =>
            declineTableRequest(
              tableId,
              tableName,
              targetModelId,
              requesterId,
              notificationData.values_only === true,
            ),
          "declined",
          "Failed to decline table request:",
        );
      }
    } else if (
      notificationType === NotificationTypeJS.TableOwnershipTransferRequest
    ) {
      const notificationId = notification.notification_id_js();
      await processRequest(
        () => respondToTransferOffer(notificationId, { type: "Reject" }),
        "declined",
        "Failed to decline transfer offer:",
      );
    }
  };

  const handleResourceClick = (
    resourceType: "model" | "team" | "table" | "org" | "space",
    resourceId: string,
    tableId?: string,
  ) => {
    if (resourceType === "model" || resourceType === "table") {
      const url = tableId
        ? `/canvas/${resourceId}?table=${encodeURIComponent(tableId)}`
        : `/canvas/${resourceId}`;
      window.open(url, "_blank", "noopener");
    } else if (resourceType === "team") {
      onNavigateToTeam?.();
      void (async () => {
        try {
          await fetchTeamsList({ showLoading: false, refresh: true });
        } catch (error) {
          log.error("Failed to refresh teams before navigation", error);
        } finally {
          refreshTeams();
          navigate(`/teams/${resourceId}`);
        }
      })();
    } else if (resourceType === "org") {
      // The notice is about the roster, so land on the tab that fixes it.
      navigate(`/organizations/${resourceId}#members`);
    } else if (resourceType === "space") {
      // Where Assign manager lives — the notice exists to get someone there.
      navigate(`/spaces/${resourceId}`);
    } else {
      log.debug(`unimplemented resource type : ${resourceType}: ${resourceId}`);
    }
  };

  const handlePickerSelect = (modelId: string) => {
    if (pickerMode === "copy") {
      handleSelectModelForCopy(modelId);
    } else {
      handleSelectModelForTransfer(modelId);
    }
  };

  return (
    <li
      className={`rounded-app py-2 px-4 mx-0 transition-colors hover:bg-list-hover ${
        isUnread ? "bg-list-selected-light" : ""
      }`}
    >
      <div className="flex items-center gap-4">
        <div className={`shrink-0 flex items-center justify-center`}>
          <NotificationIcon notificationType={notificationType} />
        </div>

        <div className="flex-1 min-w-0 overflow-hidden flex flex-col gap-1">
          <div className="wrap-break-word text-fg-regular cursor-default">
            <NotificationMessage
              notification={notification}
              onResourceClick={handleResourceClick}
            />
          </div>

          <div className="text-fg-muted-medium flex justify-between">
            <span>
              <RelativeDate
                showCreated={false}
                date={new Date(Number(notification.created_at_js()) * 1000)}
              />
            </span>
          </div>

          <div ref={anchorRef}>
            <RequestActions
              notification={notification}
              isProcessing={isProcessing}
              onAccept={handleAcceptRequest}
              onDecline={handleDeclineRequest}
              localRequestStatus={localRequestStatus}
            />
            {notificationType ===
              NotificationTypeJS.TableScheduledForDeletion &&
              (isCopyableNotification && canCopy ? (
                <div className="flex items-center gap-2 min-h-7">
                  <CopyActionButton
                    status={copyStatus}
                    onCopy={handleOpenCopyPicker}
                  />
                </div>
              ) : !isCopyableNotification ? (
                <div className="flex items-center gap-2 min-h-7">
                  <span className="px-2 py-1 text-accent-regular bg-accent-15 text-xs capitalize">
                    {isTableDeleted ? "table deleted" : "cancelled"}
                  </span>
                </div>
              ) : null)}
          </div>
        </div>

        {(isUnread || showDoneIcon) && (
          <div
            className="shrink-0 flex items-center justify-center w-2 h-2 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              if (isUnread) {
                onClick();
              }
            }}
          >
            {showDoneIcon ? (
              <DoneIcon
                className=" w-2 h-2
               text-accent"
              />
            ) : (
              <div className="w-2 h-2 bg-accent rounded-full hover:bg-accent-hover transition-colors"></div>
            )}
          </div>
        )}
      </div>

      <ModelPicker
        open={showModelPicker}
        onClose={() => setShowModelPicker(false)}
        onSelect={handlePickerSelect}
        anchorRef={anchorRef}
        title={
          pickerMode === "copy"
            ? "Choose a model to copy the table into:"
            : "Choose a model to receive the table:"
        }
        suggestedModelIds={importingModelIds}
        stopPropagation
      />
    </li>
  );
};
